// ProjectViewModal.tsx — Sprint 12. Read-only Descriptions modal +
// Budget Progress card + Documents Timeline card.
import React from 'react';
import { Button, Card, Descriptions, Modal, Progress, Space, Tag, Timeline } from 'antd';
import {
  CalendarOutlined, CheckCircleOutlined, DollarOutlined, EditOutlined, UserOutlined,
} from '@ant-design/icons';
import type { Project } from './projectTypes';
import { calculateBudgetProgress, getPriorityTag, getStatusTag } from './projectRules';

import { formatDate } from '../../utils/date';
export interface ProjectViewModalProps {
  open:      boolean;
  project:   Project | null;
  canManage: boolean;
  onClose:   () => void;
  onEdit:    (project: Project) => void;
}

export const ProjectViewModal: React.FC<ProjectViewModalProps> = ({
  open, project, canManage, onClose, onEdit,
}) => {
  const overBudget = !!(project?.actualCost && project.actualCost > project.budget);
  const variance   = project?.actualCost
    ? overBudget
      ? `Over budget by $${(project.actualCost - project.budget).toLocaleString()}`
      : `Under budget by $${(project.budget - project.actualCost).toLocaleString()}`
    : 'No actual cost recorded';

  return (
    <Modal
      title="Project Details"
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        canManage && project && (
          <Button
            key="edit" type="primary" icon={<EditOutlined />}
            onClick={() => onEdit(project)}
          >
            Edit Project
          </Button>
        ),
      ]}
    >
      {project && (
        <div>
          <Descriptions bordered column={2} className="mb-6">
            <Descriptions.Item label="Project Name" span={2}>
              <strong>{project.name}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Status">{getStatusTag(project.status)}</Descriptions.Item>
            <Descriptions.Item label="Priority">{getPriorityTag(project.priority)}</Descriptions.Item>
            <Descriptions.Item label="Category">
              <Tag color="blue">{project.category.toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Responsible Person">
              <Space><UserOutlined />{project.responsiblePerson}</Space>
            </Descriptions.Item>
            <Descriptions.Item label="Budget">
              <Space><DollarOutlined />${project.budget.toLocaleString()}</Space>
            </Descriptions.Item>
            <Descriptions.Item label="Actual Cost">
              <Space><DollarOutlined />${(project.actualCost || 0).toLocaleString()}</Space>
            </Descriptions.Item>
            <Descriptions.Item label="Budget Variance">
              <Tag color={overBudget ? 'red' : 'green'}>{variance}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              <CalendarOutlined /> {formatDate(project.startDate)}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              <CheckCircleOutlined /> {formatDate(project.endDate)}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {project.description}
            </Descriptions.Item>
            {project.notes && (
              <Descriptions.Item label="Notes" span={2}>{project.notes}</Descriptions.Item>
            )}
          </Descriptions>

          {project.actualCost && (
            <Card title="Budget Progress" size="small" className="mt-4">
              <Progress
                percent={calculateBudgetProgress(project.budget, project.actualCost)}
                status={overBudget ? 'exception' : 'active'}
                format={(percent) => `${percent?.toFixed(1)}% of budget used`}
              />
              <div className="mt-2 text-sm text-gray-500">
                {overBudget ? (
                  <span className="text-red-500">
                    ⚠️ Project is over budget by ${(project.actualCost - project.budget).toLocaleString()}
                  </span>
                ) : project.actualCost < project.budget ? (
                  <span className="text-green-500">
                    ✓ Project is under budget by ${(project.budget - project.actualCost).toLocaleString()}
                  </span>
                ) : (
                  <span>Project is on budget</span>
                )}
              </div>
            </Card>
          )}

          {project.documents && project.documents.length > 0 && (
            <Card title="Documents" size="small" className="mt-4">
              <Timeline>
                {project.documents.map((doc, idx) => (
                  <Timeline.Item key={idx}>
                    <a href={doc} target="_blank" rel="noopener noreferrer">
                      Document {idx + 1}
                    </a>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          )}
        </div>
      )}
    </Modal>
  );
};
